import React, { useEffect } from 'react';
import '../../assets/css/VaSiSubscriberGuidelines.css';

function VaSiSubscriberGuidelines() {
    useEffect(() => {
        document.title = `VaSi Subscriber Guidelines | VasiApp`;
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="position-rel vasi-subscriber-page">
            <div className="background-image"></div>
            <div className="outer-page-color">
                <div className="container py-5">
                    <div className="row justify-content-center">
                        <div className="col-12 col-lg-10">

                            <header className="margin-top-8 mb-4">
                                <div className="text-center px-3 px-md-4">
                                    <h2 className="font-weight-700 padding-top-10 mb-0">
                                        VaSi Subscriber Guidelines <br />
                                        (By Learnee Inc. – applies to all monetizing creators on VaSi)
                                    </h2>
                                </div>
                            </header>

                            <aside className="inner_pages">
                                <div className="px-3 px-md-4 px-lg-5 py-3 vasi-subscriber-content">
                                    <div className="row gx-3 refer-earn">
                                        <div className="col-12">

                                            <p>
                                                These guidelines explain how subscribing to a Creator’s profile works — what you get, how billing and renewals function, and what’s expected of you as a Subscriber.
                                            </p>

                                            <div className="vasi-subscriber-quote">
                                                <p>
                                                    This page summarizes the monetization system. For full legal terms, refer to the{" "}
                                                    <a
                                                        className="vasi-subscriber-tos-link"
                                                        href="https://www.learnee.ai/terms-of-service-usa"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        Terms of Service (USA)
                                                    </a>{" "}
                                                    and{" "}
                                                    <a
                                                        className="vasi-subscriber-tos-link"
                                                        href="https://www.learnee.ai/terms-of-service-india"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        Terms of Service (India)
                                                    </a>.
                                                </p>
                                            </div>

                                            <h3>1. What Is a Profile Subscription?</h3>

                                            <p>
                                                A Profile Subscription gives you <strong>unlimited access</strong> to all paid Dots created by a specific Creator — both existing and future ones — for as long as your subscription stays active.
                                            </p>
                                            <div className="vasi-subscriber-quote">
                                                <p>
                                                    Subscriptions turn one-time purchases into full access: one payment unlocks everything that Creator shares.
                                                </p>
                                            </div>

                                            <p>
                                                Free Dots are always available to everyone on VaSi, whether you subscribe or not.
                                            </p>

                                            <h3>2. How It Works</h3>

                                            <p>
                                                Each Creator sets a monthly price for their profile (starting at <strong>$1.49 USD</strong>, or local equivalent).
                                            </p>
                                            <p>
                                                You can subscribe to any monetized profile through in-app purchase via Apple or Google.
                                            </p>
                                            <p>
                                                Your subscription <strong>renews automatically every month</strong> unless you cancel it through your app store account.
                                            </p>
                                            <p>
                                                Cancelling mid-cycle still keeps your access until the end of your paid period.
                                            </p>
                                            <div className="vasi-subscriber-quote">
                                                <p>
                                                    Manage all your subscriptions directly from your Apple or Google account settings. VaSi itself doesn’t handle in-app billing or cancellations.
                                                </p>
                                            </div>
                                            <h3>3. What You Get as a Subscriber</h3>

                                            <p>When you subscribe, you gain:</p>
                                            <ul>
                                                <li><strong>Full access</strong> to all paid Dots published by that Creator.</li>
                                                <li><strong>Instant access</strong> to any new paid Dots they publish while your subscription is active.</li>
                                                <li>The ability to <strong>save, revisit, and learn</strong> from a Creator’s entire collection of content.</li>
                                                <li>Upcoming interactive features (like commenting or messaging) when available.</li>
                                            </ul>
                                            <div className="vasi-subscriber-quote">
                                                <p>
                                                    You’re supporting the Creator directly — most of your payment goes to them.
                                                </p>
                                            </div>

                                            <h3>4. Pricing and Renewals</h3>

                                            <p>
                                                The price you see at checkout is the price you’ll pay monthly until you cancel.
                                            </p>
                                            <p>
                                                If the Creator later changes their price:
                                            </p>
                                            <ul>
                                                <li><strong>New subscribers</strong> pay the updated price.</li>
                                                <li><strong>Existing subscribers</strong> stay locked at the original price they joined with (as long as their subscription remains active).</li>
                                            </ul>
                                            <p>
                                                If you cancel and re-subscribe later, you’ll be charged the current active price.
                                            </p>
                                            <div className="vasi-subscriber-quote">
                                                <p>
                                                    Staying subscribed can save you from future price increases.
                                                </p>
                                            </div>

                                            <h3>5. Refunds and Chargebacks</h3>

                                            <p>
                                                Refunds are handled by <strong>Apple or Google</strong> under their app-store refund policies.
                                            </p>
                                            <p>
                                                Learnee doesn’t directly issue refunds or adjust subscriptions outside of those systems.
                                            </p>
                                            <p>
                                                If your refund is approved, your access will end immediately once the refund is processed.
                                            </p>
                                            <div className="vasi-subscriber-quote">
                                                <p>
                                                    Refunds affect Creator earnings, so please request them only for genuine issues (e.g., accidental purchase or duplicate billing).
                                                </p>
                                            </div>
                                            <h3>6. Fair Use and Subscriber Conduct</h3>

                                            <p>We expect Subscribers to enjoy Dots responsibly and respect Creator work:</p>
                                            <ul>
                                                <li>Don’t copy, resell, or repost a Creator’s paid Dots elsewhere.</li>
                                                <li>Don’t share screenshots or redistribute private Dot content.</li>
                                                <li>Don’t use VaSi to solicit or impersonate creators.</li>
                                                <li>Don’t exploit access for automated or commercial reuse.</li>
                                                <li>Violating these conditions can result in immediate <strong>termination of your subscription</strong> or account suspension.</li>
                                            </ul>
                                            <div className="vasi-subscriber-quote">
                                                <p>
                                                    Treat Creator content like you would a paid course or book — it’s for your personal learning, not redistribution.
                                                </p>
                                            </div>
                                            <h3>7. Cancellations</h3>

                                            <p>
                                                You can cancel your subscription any time through your app-store settings.
                                            </p>
                                            <p>After cancellation:</p>
                                            <ul>
                                                <li>You’ll keep full access until the current billing cycle ends.</li>
                                                <li>Once it expires, you’ll lose access to all paid Dots.</li>
                                                <li>You can re-subscribe any time to regain access.</li>
                                            </ul>
                                            <div className="vasi-subscriber-quote">
                                                <p>
                                                    Cancelling through VaSi directly won’t stop auto-renewal — use your app store account.
                                                </p>
                                            </div>
                                            <h3>8. Content and Safety</h3>

                                            <p>
                                                VaSi reviews monetized content to maintain safety and trust.
                                            </p>
                                            <p>
                                                If a Creator’s content violates VaSi’s <strong>Dot Policy</strong>, that Creator may lose access to monetization.
                                            </p>
                                            <p>
                                                In such cases, your subscription will automatically end, and app-store refund policies may apply.
                                            </p>
                                            <div className="vasi-subscriber-quote">
                                                <p>
                                                    If you see harmful or plagiarized content, you can report it directly from the Dot or profile page.
                                                </p>
                                            </div>

                                            <h3>9. Legal Reminder</h3>

                                            <p>
                                                This document is for convenience and clarity.
                                            </p>
                                            <p>
                                                It <strong>does not replace</strong> the full <strong>Learnee Terms of Service</strong>, which legally governs all monetization activity.
                                            </p>
                                            <div className="vasi-subscriber-quote">
                                                <p>
                                                    Please read the full{" "}
                                                    <a
                                                        className="vasi-subscriber-tos-link"
                                                        href="https://www.learnee.ai/terms-of-service-usa"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        Terms of Service (USA)
                                                    </a>
                                                    {" "}and{" "}
                                                    <a
                                                        className="vasi-subscriber-tos-link"
                                                        href="https://www.learnee.ai/terms-of-service-india"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        Terms of Service (India)
                                                    </a>{" "}
                                                    to understand your contractual rights and obligations.
                                                </p>
                                            </div>

                                        </div>
                                    </div>
                                </div>
                            </aside>

                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default VaSiSubscriberGuidelines;
